/**
 * Hosted brand assets (spec 2026-09-27 D10).
 *
 * The Ruby Token mark is ONE hosted SVG per variant on the brand image CDN:
 *
 *   <origin>/brand/ruby-token.svg               static, >= 32px
 *   <origin>/brand/ruby-token-sm.svg            static, 20-31px (no spokes, no nodes)
 *   <origin>/brand/ruby-token-xs.svg            static, < 20px (crown, girdle, pavilion)
 *   <origin>/brand/ruby-token-animated.svg      "working" cut loop, >= 32px
 *   <origin>/brand/ruby-token-animated-sm.svg   "working" cut loop, 20-31px
 *   <origin>/brand/ruby-token-animated-xs.svg   "working" cut loop, < 20px
 *
 * `<origin>` is picked per stage — image.dev.rubytill.com on dev and
 * localhost, image.rubytill.com otherwise. Never a hardcoded dev host.
 *
 * Deliberately standalone (no apiService import): the mark is drawn by every
 * loader in the app, so this module must be importable from any component
 * without dragging the API layer — or an import cycle — in behind it.
 */

export type BrandStage = 'dev' | 'prod';

const ORIGINS: Record<BrandStage, string> = {
  dev: 'https://image.dev.rubytill.com',
  prod: 'https://image.rubytill.com',
};

const ambientViteStage = (): string => {
  try {
    return (import.meta as any)?.env?.VITE_STAGE ?? '';
  } catch {
    return '';
  }
};

/**
 * Stage for brand assets. VITE_STAGE wins; otherwise the hostname decides
 * (localhost and any `dev.` host → dev). Staging reads the prod asset — the
 * brand mark is identical on every stage and staging has no image CDN of its own.
 */
export function brandStage(hostname?: string, viteStage?: string): BrandStage {
  const vs = viteStage ?? ambientViteStage();
  if (vs === 'dev') return 'dev';
  if (vs === 'prod' || vs === 'staging') return 'prod';
  const host = hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '');
  if (host === 'localhost' || host === '127.0.0.1' || host.includes('dev.')) return 'dev';
  return 'prod';
}

/** Below this rendered size the `-sm` cut (no spokes, no nodes) is used. */
export const RUBY_TOKEN_SM_BELOW_PX = 32;
/** Below this rendered size the `-xs` cut (no table either) is used. */
export const RUBY_TOKEN_XS_BELOW_PX = 20;

export function rubyTokenUrl(size: number, drawing: boolean, stage: BrandStage = brandStage()): string {
  const tier = size < RUBY_TOKEN_XS_BELOW_PX ? '-xs' : size < RUBY_TOKEN_SM_BELOW_PX ? '-sm' : '';
  const name = `ruby-token${drawing ? '-animated' : ''}${tier}.svg`;
  return `${ORIGINS[stage]}/brand/${name}`;
}
